import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { isIOS } from "react-device-detect";

// 기본 헤더용
import logoBlue from "../assets/images/logo_blue.png";
import buttonHamburgerSvg from "../assets/images/icons/button_hamburger.svg";
import "../assets/css/product.scss";

// 랜딩/서브페이지 헤더용
import svgLogo from "../assets/images/landing/logo.svg";
import "../assets/css/app_landing_v2.scss";

/**
 * Header 컴포넌트
 * - variant="default" (기본): 기존 product-header 스타일
 * - variant="landing": 랜딩 페이지 전용 투명 헤더 스타일
 * - variant="subpage": 서브 페이지 (뉴스, 구독) — 흰 배경 + 새 디자인 헤더
 * - variant="educlip": 에듀클립 페이지 — 투명→스크롤시 흰 배경
 */
const APP_STORE_URL = "https://apps.apple.com/kr/app/키즈다/id6738370393";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.aslo.smartview.donga.kids";

function Header({ variant = "default", scrolled = false, onDark = false }) {
  // const appDownloadUrl = isIOS ? APP_STORE_URL : PLAY_STORE_URL;
  const appDownloadUrl = "/intro";
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const menuRef = useRef();
  const location = useLocation();

  // 바깥 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
        setIsMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentPath = location.pathname;

  // ─── 새 디자인 헤더 (landing + subpage + educlip 공통) ───
  if (variant === "landing" || variant === "subpage" || variant === "educlip") {
    const headerClass = [
      "header",
      variant === "subpage" ? "header-scrolled-news" : "",
      variant === "educlip" ? "header-scrolled-edu" : "",
      scrolled ? "header-scrolled" : "",
      onDark ? "header-on-dark" : "",
    ]
      .filter(Boolean)
      .join(" ");

    // 에듀클립: 스크롤 시 흰 배경 (PC에서만 적용)
    const headerStyle =
      variant === "educlip"
        ? scrolled
          ? {
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
            }
          : { background: "transparent" }
        : undefined;

    return (
      <header className={headerClass} style={headerStyle} ref={menuRef}>
        <div className="header-inner">
          <a href="/" className="logo">
            <img src={svgLogo} alt="키즈다" className="logo-svg" />
          </a>

          <nav className="nav">
            <a
              href="/"
              className={`nav-link ${currentPath === "/" || currentPath.startsWith("/kizda_news") ? "active-link" : ""}`}
            >
              키즈다 뉴스
            </a>
            <a
              href="/intro"
              className={`nav-link ${currentPath === "/intro" ? "active-link" : ""}`}
            >
              서비스 소개
            </a>
            <a
              href="/news"
              className={`nav-link ${currentPath === "/news" ? "active-link" : ""}`}
            >
              키즈다 소식
            </a>
            <a
              href="/brainlab"
              className={`nav-link ${currentPath === "/brainlab" ? "active-link" : ""}`}
            >
              브레인랩
            </a>
            <a
              href="/product"
              className={`nav-link ${currentPath === "/product" ? "active-link" : ""}`}
            >
              구독
            </a>
            {/* 에듀클립 메뉴 숨김 처리
            <a
              href="/educlip"
              className={`nav-link ${currentPath === "/educlip" ? "active-link" : ""}`}
            >
              에듀클립
            </a>
            */}
          </nav>

          <a href={appDownloadUrl} className="nav-cta" rel="noreferrer">
            앱 다운로드
          </a>

          {/* 모바일 햄버거 */}
          <button
            className={`mobile-menu-btn ${isMobileOpen ? "open" : ""}`}
            aria-label="메뉴"
            onClick={() => setIsMobileOpen(!isMobileOpen)}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>

        {/* 모바일 메뉴 */}
        <div className={`mobile-nav ${isMobileOpen ? "open" : ""}`}>
          <a
            className={`mobile-nav-link ${currentPath === "/" || currentPath.startsWith("/kizda_news") ? "active-link" : ""}`}
            href="/"
          >
            키즈다 뉴스
          </a>
          <a
            className={`mobile-nav-link ${currentPath === "/intro" ? "active-link" : ""}`}
            href="/intro"
          >
            서비스 소개
          </a>
          <a
            className={`mobile-nav-link ${currentPath === "/news" ? "active-link" : ""}`}
            href="/news"
          >
            키즈다 소식
          </a>
          <a
            className={`mobile-nav-link ${currentPath === "/brainlab" ? "active-link" : ""}`}
            href="/brainlab"
          >
            브레인랩
          </a>
          <a
            className={`mobile-nav-link ${currentPath === "/product" ? "active-link" : ""}`}
            href="/product"
          >
            구독
          </a>
          {/* 에듀클립 메뉴 숨김 처리
          <a
            className={`mobile-nav-link ${currentPath === "/educlip" ? "active-link" : ""}`}
            href="/educlip"
          >
            에듀클립
          </a>
          */}
          <a
            href={appDownloadUrl}
            className="mobile-nav-link mobile-nav-cta"
            rel="noreferrer"
          >
            앱 다운로드
          </a>
        </div>
      </header>
    );
  }

  // ─── 기본 헤더 (다른 모든 페이지) ───
  return (
    <div className="product-header" ref={menuRef}>
      <ul>
        <li>
          <a href="/">
            <img src={logoBlue} alt="" />
          </a>
        </li>

        <li className="menu">
          <div className={currentPath === "/intro" ? "on" : ""}>
            <a href="/intro">키즈다 소개</a>
          </div>
          <div className={currentPath === "/product" ? "on" : ""}>
            <a href="/product">구독</a>
          </div>
          {/* 에듀클립 메뉴 숨김 처리
          <div className={currentPath === "/educlip" ? "on" : ""}>
            <a href="/educlip">에듀클립</a>
          </div>
          */}
        </li>

        <li className="hamburger" onClick={() => setIsOpen(!isOpen)}>
          <img src={buttonHamburgerSvg} alt="" />
        </li>

        <li className={`mobile-slide-menu ${isOpen ? "open" : ""}`}>
          <div className="close-button" onClick={() => setIsOpen(false)}>
            &times;
          </div>
          <div className={currentPath === "/intro" ? "on" : ""}>
            <a href="/intro">키즈다 소개</a>
          </div>
          <div className={currentPath === "/product" ? "on" : ""}>
            <a href="/product">구독</a>
          </div>
          {/* 에듀클립 메뉴 숨김 처리
          <div className={currentPath === "/educlip" ? "on" : ""}>
            <a href="/educlip">에듀클립</a>
          </div>
          */}
        </li>

        {isOpen && <div className="overlay" onClick={() => setIsOpen(false)} />}
      </ul>
    </div>
  );
}

export default Header;
